import React, { useCallback } from 'react'

import {
  ArrowLeft,
  AttentionOutlined,
  Button,
  Checkbox,
  Icon,
  InfoOutlined,
  Modal,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { bool, func, node, number, shape, string } from 'prop-types'

import I18n from 'Pods/Shared/I18n'

import FeeBreakdown from './FeeBreakdown'
import StatusBanner from '../StatusBanner'

function CancelStep({
  isOpen,
  onClose,
  onSubmit,
  onBack,
  hasReasonStep = false,
  hasFee = false,
  hasFeeError = false,
  feeBreakdown = null,
  accessUntilFormatted = null,
  showAccessUntilNotice = false,
  isAcknowledged = false,
  onAcknowledgeChange,
  isSubmitDisabled = false,
  isSubmitting = false,
  consequencesList = null,
  reasonSelect = null,
  locale = 'en',
}) {
  const translate = useCallback(
    (key, options = {}) => I18n.t(
      `subscription_management.cancel_modal.${key}`,
      { ns: 'rails', lng: locale, ...options }
    ),
    [locale]
  )

  // Back / Keep / Cancel footer for the v1.6 consequences screen (design 86car5fhk);
  // flag-off keeps the Triangle primary/secondary footer below.
  const footer = hasReasonStep ? (
    <div className="cancel-subscription-modal__step-footer">
      <Button
        type="ghost"
        size="small"
        actionType="button"
        icon={<Icon svgComponent={ArrowLeft} size="tiny" color="dark" />}
        onClick={onBack}
        disabled={isSubmitting}
      >
        {translate('back_button')}
      </Button>
      <div className="cancel-subscription-modal__footer-actions">
        <Button
          type="ghost-primary"
          size="small"
          actionType="button"
          onClick={onClose}
          disabled={isSubmitting}
        >
          {translate('keep_subscription_button')}
        </Button>
        <Button
          type="primary-warning"
          size="small"
          actionType="button"
          onClick={onSubmit}
          disabled={isSubmitDisabled}
        >
          {translate('submit_button')}
        </Button>
      </div>
    </div>
  ) : null

  const secondaryAction = hasFee
    ? {
      content: translate('keep_subscription_button'),
      buttonProps: { onClick: onClose, disabled: isSubmitting },
    }
    : undefined

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      header={translate(hasFee ? 'fee_title_header' : 'title')}
      width={hasFee ? 'medium' : 'small'}
      canClose
      // Closing while the cancellation is in flight would report a backout to the user
      // while the request still lands (and reset isSubmitting, allowing a second POST on
      // reopen). Blocked at the Modal level for the same reason as the pause step: a no-op
      // onClose desyncs Triangle's internal state from the parent.
      disableClose={isSubmitting}
      customClass={
        `cancel-subscription-modal${hasFee ? ' cancel-subscription-modal--with-fee' : ''}`
      }
      footer={footer}
      primaryAction={hasReasonStep ? undefined : {
        content: translate('submit_button'),
        buttonProps: {
          type: 'primary-warning',
          onClick: onSubmit,
          disabled: isSubmitDisabled,
        },
      }}
      secondaryAction={hasReasonStep ? undefined : secondaryAction}
    >
      <div className="cancel-subscription-modal__body">
        {hasFeeError && (
          <StatusBanner
            icon={AttentionOutlined}
            description={translate('fee_error')}
            variant="danger"
          />
        )}
        {hasFee && (
          <StatusBanner
            icon={AttentionOutlined}
            description={(
              // Translation HTML is sourced from local bundles and limited to
              // trusted copy updates (bolds the fee phrase, UsageTracker precedent).
              // eslint-disable-next-line react/no-danger
              <span dangerouslySetInnerHTML={{ __html: translate('fee_warning_banner') }} />
            )}
            variant="attention"
          />
        )}
        {showAccessUntilNotice && (
          <StatusBanner
            icon={InfoOutlined}
            description={translate('no_fee_description', {
              date: accessUntilFormatted,
            })}
            variant="attention"
          />
        )}
        {consequencesList}
        {hasFee && (
          <>
            <FeeBreakdown
              remainingMonths={feeBreakdown.remainingMonths}
              monthlyRate={feeBreakdown.monthlyRate}
              totalRemaining={feeBreakdown.totalRemaining}
              cancelFee={feeBreakdown.cancelFee}
              accessUntilFormatted={accessUntilFormatted}
              locale={locale}
            />
            <div className="cancel-subscription-modal__acknowledgement">
              <Checkbox
                id="cancel-fee-acknowledge"
                isChecked={isAcknowledged}
                isControlled
                label={translate('acknowledge_checkbox')}
                onChange={onAcknowledgeChange}
                size="medium"
              />
            </div>
          </>
        )}
        {reasonSelect}
      </div>
    </Modal>
  )
}

CancelStep.propTypes = {
  isOpen: bool.isRequired,
  onClose: func.isRequired,
  onSubmit: func.isRequired,
  onBack: func.isRequired,
  hasReasonStep: bool,
  hasFee: bool,
  hasFeeError: bool,
  feeBreakdown: shape({
    remainingMonths: number,
    monthlyRate: number,
    totalRemaining: number,
    cancelFee: number,
  }),
  accessUntilFormatted: string,
  showAccessUntilNotice: bool,
  isAcknowledged: bool,
  onAcknowledgeChange: func.isRequired,
  isSubmitDisabled: bool,
  isSubmitting: bool,
  consequencesList: node,
  reasonSelect: node,
  locale: string,
}

export default CancelStep
